import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { API_BASE_URL } from '../config';

const TOKEN_KEY = 'nbba_access_token';
const REFRESH_TOKEN_KEY = 'nbba_refresh_token';

interface TokenPayload {
  user_id: number;
  email: string;
  username: string;
  exp: number;
}

interface AuthResponse {
  user: {
    id: number;
    email: string;
    username: string;
    created_at: string;
    last_login?: string;
  };
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

// Create axios instance with auth interceptors
export const authAxios = axios.create({
  baseURL: API_BASE_URL,
});

// Request interceptor - add auth header
authAxios.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle 401 and refresh token
let isRefreshing = false;
let failedQueue: { resolve: (token: string) => void; reject: (error: any) => void }[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else if (token) {
      promise.resolve(token);
    }
  });
  failedQueue = [];
};

authAxios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 and not already retrying
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Wait for refresh to complete
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(authAxios(originalRequest));
            },
            reject,
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newTokens = await refreshTokens();
        if (newTokens) {
          processQueue(null, newTokens.access_token);
          originalRequest.headers.Authorization = `Bearer ${newTokens.access_token}`;
          return authAxios(originalRequest);
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export function getAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function setTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem(TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

export function clearTokens(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

export function isTokenExpired(token: string): boolean {
  try {
    const decoded = jwtDecode<TokenPayload>(token);
    // Check if token expires in the next 60 seconds
    return decoded.exp * 1000 < Date.now() + 60000;
  } catch {
    return true;
  }
}

export function getUserFromToken(token: string): { id: number; email: string; username: string } | null {
  try {
    const decoded = jwtDecode<TokenPayload>(token);
    return {
      id: decoded.user_id,
      email: decoded.email,
      username: decoded.username,
    };
  } catch {
    return null;
  }
}

export async function login(email: string, password: string): Promise<AuthResponse> {
  const response = await axios.post<AuthResponse>(`${API_BASE_URL}/auth/login`, {
    email,
    password,
  });
  setTokens(response.data.access_token, response.data.refresh_token);
  return response.data;
}

export async function signup(email: string, username: string, password: string): Promise<AuthResponse> {
  const response = await axios.post<AuthResponse>(`${API_BASE_URL}/auth/signup`, {
    email,
    username,
    password,
  });
  setTokens(response.data.access_token, response.data.refresh_token);
  return response.data;
}

export async function refreshTokens(): Promise<AuthResponse | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return null;
  }

  try {
    const response = await axios.post<AuthResponse>(`${API_BASE_URL}/auth/refresh`, {
      refresh_token: refreshToken,
    });
    setTokens(response.data.access_token, response.data.refresh_token);
    return response.data;
  } catch {
    clearTokens();
    return null;
  }
}

export async function logout(): Promise<void> {
  const refreshToken = getRefreshToken();
  if (refreshToken) {
    try {
      await authAxios.post('/auth/logout', { refresh_token: refreshToken });
    } catch {
      // Ignore errors during logout
    }
  }
  clearTokens();
}

export async function getCurrentUser(): Promise<AuthResponse['user'] | null> {
  const token = getAccessToken();
  if (!token) {
    return null;
  }

  try {
    const response = await authAxios.get<AuthResponse['user']>('/auth/me');
    return response.data;
  } catch {
    return null;
  }
}

export function getGoogleAuthUrl(): string {
  return `${API_BASE_URL}/auth/google`;
}
